import { Filter } from "bad-words";
import React from "react";

const filter = new Filter();

// Extra hate-speech / slurs to block on top of the default list
const EXTRA_BAD = [
  "nigger", "nigga", "faggot", "fag", "tranny", "kike", "spic", "chink",
  "gook", "wetback", "raghead", "towelhead", "dyke", "retard", "retarded",
  "kill", "murder", "rape", "suicide",
];
try {
  filter.addWords(...EXTRA_BAD);
} catch {
  /* ignore */
}

export const containsProfanity = (text: string): boolean => {
  try {
    return filter.isProfane(text);
  } catch {
    return false;
  }
};

/** Returns the list of offending words found in the given text (deduped, lower-cased). */
export const findProfaneWords = (text: string): string[] => {
  if (!text) return [];
  const tokens = text.split(/[^\p{L}\p{N}']+/u).filter(Boolean);
  const found = new Set<string>();
  for (const t of tokens) {
    try {
      if (filter.isProfane(t)) found.add(t.toLowerCase());
    } catch {
      /* ignore */
    }
  }
  return Array.from(found);
};

export const cleanText = (text: string): string => {
  try {
    return filter.clean(text);
  } catch {
    return text;
  }
};

const isWordProfane = (word: string): boolean => {
  if (!word || !/\w/.test(word)) return false;
  try {
    return filter.isProfane(word);
  } catch {
    return false;
  }
};

interface CensoredTextProps {
  text: string;
  className?: string;
}

/**
 * Renders text with any offensive words visually blurred out.
 * Users can still post anything, but vulgar/hate-speech terms are obscured at read time.
 */
export const CensoredText: React.FC<CensoredTextProps> = ({ text, className }) => {
  if (!text) return null;
  // Split keeping whitespace and punctuation as separators so we preserve formatting
  const parts = text.split(/([^\p{L}\p{N}']+)/u);
  return React.createElement(
    React.Fragment,
    null,
    parts.map((part, i) => {
      if (isWordProfane(part)) {
        return React.createElement(
          "span",
          {
            key: i,
            className:
              "inline-block align-baseline blur-[5px] hover:blur-[5px] select-none bg-muted/40 rounded px-0.5",
            "aria-label": "offensive language hidden",
            title: "Offensive language hidden",
          },
          part
        );
      }
      return React.createElement(React.Fragment, { key: i }, part);
    })
  );
};
